import { useMemo } from 'react';
import { formatCurrency } from '../utils';

export default function DashboardBudgetSummary({ projects, enabledFeatures, onNavigate }) {
  const { totalEstimated, totalSpent, totalRemaining, pctSpent, projectBudgets } = useMemo(() => {
    let totalEstimated = 0, totalSpent = 0;
    const projectBudgets = [];
    for (const p of projects) {
      let est = 0, spent = 0;
      for (const t of (p.tasks || [])) {
        est += t.estimatedCost || 0;
        spent += t.actualCost || 0;
      }
      if (est > 0 || spent > 0) {
        const pctUsed = est > 0 ? Math.round((spent / est) * 100) : 0;
        projectBudgets.push({
          id: p.id,
          name: p.name,
          estimated: est,
          spent,
          pctUsed,
          overBudget: spent > est,
        });
      }
      totalEstimated += est;
      totalSpent += spent;
    }
    projectBudgets.sort((a, b) => b.pctUsed - a.pctUsed);
    return {
      totalEstimated,
      totalSpent,
      totalRemaining: totalEstimated - totalSpent,
      pctSpent: totalEstimated > 0 ? Math.round((totalSpent / totalEstimated) * 100) : 0,
      projectBudgets,
    };
  }, [projects]);

  if (enabledFeatures?.budget === false) return null;

  function shortCurrency(amount) {
    if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
    if (Math.abs(amount) >= 1_000) return `$${Math.round(amount / 1_000)}K`;
    return formatCurrency(amount);
  }

  if (totalEstimated === 0 && projectBudgets.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Portfolio Budget</h3>
        <p className="text-xs text-slate-400">No budget data yet. Add cost estimates to tasks to see portfolio budget.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-stone-800 mb-4">Portfolio Budget</h3>
      <button onClick={() => onNavigate("budget-risk")}
        className="rounded-lg -mx-1 px-1 border border-transparent hover:border-amber-400 hover:shadow-sm transition-all w-full text-left cursor-pointer relative group">
        <span className="absolute top-1 right-2 text-[10px] font-semibold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded hidden group-hover:inline-flex items-center gap-0.5">
          → Budget Risk Report
        </span>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><p className="text-lg font-bold text-stone-700">{shortCurrency(totalEstimated)}</p><p className="text-xs text-slate-500">Estimated</p></div>
          <div><p className="text-lg font-bold text-stone-700">{shortCurrency(totalSpent)}</p><p className="text-xs text-slate-500">Spent</p></div>
          <div><p className={`text-lg font-bold ${totalRemaining >= 0 ? "text-green-600" : "text-red-600"}`}>{shortCurrency(totalRemaining)}</p><p className="text-xs text-slate-500">Remaining</p></div>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
          <div className={`h-2 rounded-full ${pctSpent > 100 ? "bg-red-500" : "bg-amber-400"}`}
            style={{ width: `${Math.min(pctSpent, 100)}%` }} />
        </div>
      </button>
      <div className="space-y-2.5">
        {projectBudgets.map(pb => (
          <div key={pb.id}>
            <button onClick={() => onNavigate("budget-detail", pb.id)}
              className="flex justify-between text-xs mb-0.5 rounded px-1 -mx-1 w-full text-left cursor-pointer hover:bg-slate-50 transition-colors group">
              <span className="text-slate-700 font-medium">{pb.name}</span>
              <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline ml-auto mr-2">→ Budget Detail</span>
              <span className={pb.overBudget ? "text-red-600 font-medium" : "text-slate-500"}>
                {shortCurrency(pb.spent)} / {shortCurrency(pb.estimated)}
              </span>
            </button>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full ${pb.overBudget ? "bg-red-500" : pb.pctUsed > 80 ? "bg-amber-400" : "bg-green-500"}`}
                style={{ width: `${Math.min(pb.pctUsed, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
